import { useEffect } from 'react';
import styled from '@emotion/styled';

import { Button, Container, Box } from '@toolbox/components';
import {
  Step,
  useCloseWidget,
  useLeadData,
  useWidgetContext,
  useZipCodeLocation,
} from '@widget/core';
import { QuestionHeader } from '../../components';
import { defaultValues } from './utils';
import { checkIfObjectContainsAtLeastOneValue } from '../../utils/objectUtils';
import { formatMcSearchLink } from './utils/formatMcSearchLink';

import type { KeyValueType } from '@widget/types/KeyValueType';
import type { LeadData, StepProps } from '@widget/core';

export type ThankYouStepV2Props = StepProps & {
  heading?: string;
  buttonText?: string;
  closeButtonText?: string;
  closeWidget?: boolean;
  enableAutomaticRedirectionToPropertySearch?: KeyValueType[];
  propertySearchLink?: string | null;
  children: React.ReactNode;
};

const HeaderWrap = styled.div`
  margin-bottom: 24px;
`;

const BodyWrap = styled.div`
  max-width: 686px;
  margin: 0 auto;
`;

export const ThankYouStepV2 = ({
  heading = defaultValues.heading,
  buttonText = defaultValues.buttonText,
  closeButtonText,
  closeWidget,
  enableAutomaticRedirectionToPropertySearch,
  propertySearchLink,
  children,
  ...stepProps
}: ThankYouStepV2Props): JSX.Element => {
  const { close, getFormData } = useWidgetContext();
  const { setLeadData } = useLeadData();
  const formData = getFormData();
  const { setIsThankYouPageShown } = useCloseWidget();
  const { zipCodeLocationData } = useZipCodeLocation();

  const redirectToMcSearch = checkIfObjectContainsAtLeastOneValue(
    formData,
    enableAutomaticRedirectionToPropertySearch,
  );

  if (redirectToMcSearch) {
    setTimeout(() => {
      //added to prevent leave / stay on page popup in firefox
      window.onbeforeunload = null;
      const link = formatMcSearchLink(propertySearchLink, zipCodeLocationData);
      link && window.location.replace(link);
    }, 1500);
  }

  useEffect(() => {
    setIsThankYouPageShown(true);
  }, [setIsThankYouPageShown]);

  const closeWidgetHandler = (): void => {
    setLeadData({} as LeadData);
    close();
  };

  return (
    <Step {...stepProps}>
      <Container>
        <HeaderWrap>
          <QuestionHeader closeButtonText={closeButtonText} title={heading} />
        </HeaderWrap>
        <BodyWrap>
          {children}
          {closeWidget ? (
            <Box>
              <Button
                fullWidth
                onClick={() => closeWidgetHandler()}
                variant="secondary"
                size={'medium'}
              >
                {buttonText}
              </Button>
            </Box>
          ) : null}
        </BodyWrap>
      </Container>
    </Step>
  );
};
